import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCheck, Archive, Circle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Enterprise Notification Centre — bell + dropdown panel.
 *
 * Polls `/notifications/inbox/unread-count` every 45s (auth users only).
 * Panel shows the last N unread + read items, grouped by category with
 * filter, mark-read, mark-all-read and archive actions.
 */
export default function NotificationBell({ variant = "light" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const loadCount = async () => {
    if (!user) return;
    try {
      const r = await api.get("/notifications/inbox/unread-count");
      setUnread(r.data.unread || 0);
    } catch { /* silent */ }
  };
  const loadItems = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const r = await api.get("/notifications/inbox", { params: { limit: 30 } });
      setItems(r.data.items || []);
      setUnread(r.data.unread || 0);
    } catch { setItems([]); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!user) return;
    loadCount();
    const t = setInterval(loadCount, 45000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [user?.user_id]);

  useEffect(() => { if (open) loadItems(); /* eslint-disable-next-line */ }, [open]);

  if (!user) return null;

  const markAll = async () => {
    try {
      await api.post("/notifications/inbox/mark-all-read");
      loadItems(); loadCount();
    } catch { /* ignore */ }
  };
  const markRead = async (id) => {
    try {
      await api.post("/notifications/inbox/mark-read", { ids: [id] });
      loadItems(); loadCount();
    } catch { /* ignore */ }
  };
  const archive = async (id) => {
    try {
      await api.post("/notifications/inbox/archive", { ids: [id] });
      loadItems(); loadCount();
    } catch { /* ignore */ }
  };

  const categories = ["all", ...Array.from(new Set(items.map(i => i.category).filter(Boolean)))];
  const visible = filter === "all" ? items : items.filter(i => i.category === filter);

  const btnClass = variant === "dark"
    ? "text-white/80 hover:text-white hover:bg-white/10"
    : "text-foreground hover:bg-muted";

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative inline-flex items-center justify-center h-9 w-9 rounded-sm ${btnClass}`}
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="notification-unread-count">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-[360px] max-h-[560px] overflow-hidden bg-white border border-border shadow-2xl rounded-sm z-40" data-testid="notification-panel">
            <div className="p-3 border-b border-border flex items-center justify-between">
              <div>
                <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground">Notifications</div>
                <div className="text-sm font-semibold">{unread} unread</div>
              </div>
              <div className="flex gap-1">
                <button onClick={markAll} className="text-[11px] mono uppercase tracking-widest px-2 py-1 border border-border hover:bg-muted rounded-sm inline-flex items-center gap-1" data-testid="notification-mark-all">
                  <CheckCheck className="h-3 w-3" /> Mark all
                </button>
              </div>
            </div>
            {categories.length > 1 && (
              <div className="px-3 pt-2 pb-2 border-b border-border flex flex-wrap gap-1">
                {categories.map(c => (
                  <button key={c} onClick={() => setFilter(c)}
                    className={`text-[10px] mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${filter === c ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                    data-testid={`notification-filter-${c}`}
                  >{c}</button>
                ))}
              </div>
            )}
            <div className="overflow-y-auto max-h-[420px]">
              {busy && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
              {!busy && visible.length === 0 && (
                <div className="p-8 text-center text-sm text-muted-foreground">You're all caught up.</div>
              )}
              {visible.map((n) => (
                <div key={n.id} data-testid={`notification-item-${n.id}`} className={`px-3 py-3 border-b border-border last:border-0 ${!n.read ? "bg-primary/5" : ""} hover:bg-muted/40`}>
                  <div className="flex items-start gap-2">
                    <Circle className={`h-2 w-2 mt-1.5 shrink-0 ${!n.read ? "text-primary fill-primary" : "text-muted-foreground"}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium truncate">{n.title || n.subject || "Notification"}</div>
                      {n.body && <div className="text-[11.5px] text-muted-foreground mt-0.5 line-clamp-2">{n.body}</div>}
                      <div className="mt-1 flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-muted-foreground">
                        {n.category && <span className="px-1 border border-border rounded-sm">{n.category}</span>}
                        <span>{fmt(n.created_at || n.ts)}</span>
                        {n.link && <Link to={n.link} onClick={() => setOpen(false)} className="text-primary hover:underline">Open →</Link>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!n.read && (
                        <button onClick={() => markRead(n.id)} title="Mark read" className="p-0.5 text-muted-foreground hover:text-primary">
                          <CheckCheck className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button onClick={() => archive(n.id)} title="Archive" className="p-0.5 text-muted-foreground hover:text-red-600">
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const fmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const s = Math.round((Date.now() - d.getTime()) / 1000);
    if (s < 60) return "just now";
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
};
